import { withFullHydrate } from '@ecommerce/nest-ssr/common';
import Box from 'atomic/utilization/box';
import Container from 'atomic/utilization/container';
import Rating from 'atomic/utilization/rating';
import AdultGuard from 'components/adult-guard/adult-guard';
import BoxBestOffersDesktop from 'components/box-best-offers/box-best-offers.desktop';
import BreadcrumbsDesktop from 'components/utilization/breadcrumbs/breadcrumbs.desktop';
import Chips from 'components/chips/chips';
import ChipsDesktop from 'components/chips/components/chips-desktop/chips-desktop';
import PartnerCookieHandler from 'components/cookies/partner-cookie-handler/partner-cookie-handler';
import ProductDetailImpression from 'components/impressions/product-impression/detail/product-detail-impression';
import MetaTagsProduct from 'components/meta-tags/meta-tags-product/meta-tags-product';
import ProductActions from 'components/product/product-actions/product-actions';
import ProductAttributesList from 'components/product/product-attributes/product-attributes-list';
import ProductAttributesTable from 'components/product/product-attributes/product-attributes-table';
import ProductBestOfferDesktop from 'components/product/product-best-offer/product-best-offer.desktop';
import ProductCookieHandler from 'components/product/product-cookie-handler/product-cookie-handler';
import ProductDescription from 'components/product/product-description/product-description';
import ProductOffers from 'components/product/product-offer/product-offers';
import ProductOffersTitle from 'components/product/product-offer/product-offers-title';
import ProductOpinionList from 'components/product/product-opinion/product-opinion-list/product-opinion-list';
import ProductOpinionSummary from 'components/product/product-opinion/product-opinion-summary/product-opinion-summary';
import ProductPhoto from 'components/product/product-photo/product-photo';
import ProductProposed from 'components/product/product-proposed/product-proposed';
import ProductSpecificationDesktop from 'components/product/product-specification/product-specification.desktop';
import ProductStructuralData from 'components/product/product-structural-data/product-structural-data';
import ProductTabs from 'components/product/product-tabs/product-tabs';
import ProductTabsContent from 'components/product/product-tabs/product-tabs-content';
import ProductTabsNavi from 'components/product/product-tabs/product-tabs-navi';
import ProductTechnologies from 'components/product/product-technologies/product-technologies';
import ProductWidgetSimilarProduct from 'components/product/product-widget-similar-product';
import RankingList from 'components/ranking/ranking-list';
import flectionNumeral from 'helpers/number';
import { useSeo } from 'hooks/use-seo';
import { ProductPageProps } from 'interfaces/product-page-props';
import MainLayoutDesktop from 'layouts/main-layout/main-layout.desktop';
import React from 'react';
import { modals } from '../../../reducers/modals';
import { offers } from '../../../reducers/offers';
import PriceAlertEmailModal from '../../components/modal/price-alert-modal/price-alert-email-modal';
import './index.desktop.scss';

const ProductDesktop: React.FC<ProductPageProps> = ({ product, breadcrumbs, proposedProducts, rankedProducts, isAdultAccepted }) => {
    const seo = useSeo();

    return (
        <MainLayoutDesktop>
            <BreadcrumbsDesktop breadcrumbs={breadcrumbs}/>
            <AdultGuard isEnabled={product.category.adult} isBlocked={!isAdultAccepted}>
                <div className="product">
                    <Container>
                        <div className="product__content-top">
                            <Container>
                                <ProductPhoto
                                    modalIsOpen={false}
                                    product={{
                                        name: product.name,
                                        photos: product.photos,
                                        attributes: product.attributes,
                                        price: product.price,
                                        offers: product.offers
                                    }}
                                    emulateTouch={false}
                                />
                                <div className="product__info">
                                    <h1 className="product__name">
                                        {seo.h1}
                                    </h1>
                                    <ProductAttributesList attributes={product.attributes}/>
                                    <Rating className="md" opinions={product.opinions}/>
                                    <ProductBestOfferDesktop/>
                                </div>

                                {product.offers.items.length !== 0 && (
                                    <ProductActions product={product}/>
                                )}
                            </Container>
                        </div>
                    </Container>
                    <Container className="product__content-main">
                        <ProductTabs>
                            <ProductTabsNavi
                                keys={['ceny', 'techniczne', 'opinie']}
                                labels={['Oferty', 'O Produkcie', 'Opinie']}
                                defaultValue={'ceny'}
                            />
                            <ProductTabsContent tabKey="ceny" isActive={true}>
                                { product.offers.count > 0 && <ProductOffers productName={seo.h1 || product.name}/> }
                                { product.offers.count === 0 && <ProductProposed proposedProducts={proposedProducts}/> }
                                { product.offers.count === 0 && rankedProducts.length > 0 && (
                                    <RankingList
                                        rankedProducts={rankedProducts}
                                        categoryName={product.category.name}
                                        showButton={true}
                                        categoryId={product.category.id}
                                    />
                                )}
                            </ProductTabsContent>
                            <ProductTabsContent tabKey="techniczne">
                                <div className="product-offers-header">
                                    <ProductOffersTitle productName={seo.h1 || product.name} description="">
                                        - opis i dane produktu
                                    </ProductOffersTitle>
                                </div>
                                {(product.description.text || product.description.html || product.description.multimedia) && (
                                    <Box title="Opis produktu" anchorIdName="opis-produktu" className="box--mb-20">
                                        <ProductDescription description={product.description}/>
                                    </Box>
                                )}
                                {product.technologies.length > 0 && (
                                    <Box title="Kluczowe technologie" anchorIdName="kluczowe-technologie" className="box--mb-20">
                                        <ProductTechnologies technologies={product.technologies}/>
                                    </Box>
                                )}
                                {product.attributes.length > 0 && (
                                    <Box title="Najważniejsze parametry" anchorIdName="attributes" className="box--mb-20"
                                        headerTitle={true}
                                    >
                                        <ProductAttributesTable attributes={product.attributes}/>
                                    </Box>
                                )}
                                {product.specification.length > 0 && (
                                    <Box title="Specyfikacja szczegółowa" anchorIdName="specification" className="box--mb-20">
                                        <ProductSpecificationDesktop specification={product.specification}/>
                                    </Box>
                                )}
                            </ProductTabsContent>
                            <ProductTabsContent tabKey="opinie">
                                <div className="product-offers-header">
                                    <ProductOffersTitle productName={seo.h1 || product.name} description="">
                                        {product.opinions.count === 0
                                            ? (
                                                <>
                                                - opinie
                                                </>
                                            )
                                            : (
                                                <a href={product.opinions.url} target="_blank" rel="noopener noreferrer"
                                                    title={product.name}
                                                >
                                                - opinie
                                                </a>
                                            )}
                                    </ProductOffersTitle>
                                </div>
                                <Box title="Ogólna ocena" anchorIdName="opinion-summery" className="box--mb-20">
                                    <ProductOpinionSummary
                                        title={product.name}
                                        url={product.opinions.url}
                                        count={product.opinions.count}
                                        average={product.opinions.average}
                                    />
                                </Box>
                                {
                                    Boolean(product.opinions.items.length) && (
                                        <Box
                                            title={`${flectionNumeral(product.opinions.count, ['opinia', 'opinie', 'opinii'])} o produkcie`}
                                            anchorIdName="opinion-list"
                                            className="box--mb-20"
                                        >
                                            <ProductOpinionList
                                                url={product.opinions.url}
                                                count={product.opinions.count}
                                                items={product.opinions.items}
                                            />
                                        </Box>
                                    )
                                }
                            </ProductTabsContent>
                            <BoxBestOffersDesktop/>
                        </ProductTabs>
                        <ProductWidgetSimilarProduct categoryUrl={product.category.url} isMobile={false} productUrl={product.url}/>
                        <Chips
                            newProducts={product.category.department.products}
                            popularProducts={product.category.products}
                            relatedCategories={product.category.subdepartment.categories}
                            popularCategories={product.category.department.categories}
                            relatedProductsFilters={product.filters.entries}
                            categoryName={product.category.name}
                            component={ChipsDesktop}
                        />
                    </Container>
                    <ProductStructuralData product={product}/>
                    <ProductDetailImpression product={product}/>
                    <PriceAlertEmailModal/>
                    <ProductCookieHandler/>
                    <PartnerCookieHandler/>
                    <MetaTagsProduct
                        productId={product.id}
                        producrName={product.producer?.name || ''}
                        offersCount={product.offers.count}
                        categoryName={product.category.name}
                        categoryDepartamentName={product.category.department.name}
                        categorySubdepartamentName={product.category.subdepartment.name}
                    />
                </div>
            </AdultGuard>
        </MainLayoutDesktop>
    );
};

export default withFullHydrate(ProductDesktop, {
    root: '#root',
    reducers: { offers, modals }
}, 'views/pages/product/index.desktop');
